import type { FC } from 'react';
import React, { useCallback, useContext, useMemo } from 'react';

import {
  EditorContext,
  EditorQueryContext,
  PluginsContext,
  RicosContext,
  ToolbarContext,
  useTrackRender,
} from './../../../ricos-context';
import type { RicosPluginAddButton } from './../../../ricos-plugins';

import InsertPluginToolbar from '../../components/InsertPluginToolbar/InsertPluginToolbar';
import { FOOTER_TOOLBAR } from './data-hooks';
import styles from './FooterToolbar-scss';
import { getToolbarIdByInsertPlugin } from './get-toolbar-id-by-insert-plugin';

export const FooterToolbar: FC = () => {
  useTrackRender('Footer toolbar');

  const plugins = useContext(PluginsContext);
  const { toolbars } = useContext(ToolbarContext);
  const { languageDir } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const editorQuery = useContext(EditorQueryContext);

  const footerSettings = toolbars.footer.getSettings()?.footerToolbarConfig;
  const onlyShownButtons = useMemo(
    () => footerSettings?.pluginsToDisplayInToolbar?.map(getToolbarIdByInsertPlugin),
    [footerSettings?.pluginsToDisplayInToolbar]
  );

  const addButtons = useMemo(() => plugins.getAddButtons(), [plugins]);

  const selection = editorQuery.selection().doc?.children?.[0];
  const disabled = plugins.isFooterToolbarDisabled(selection);
  const options = useMemo(
    () => ({
      disabled,
    }),
    [disabled]
  );

  const onButtonClick = useCallback(
    (addButton: RicosPluginAddButton, event: Event) => {
      const footerToolbarItem = addButton.toFooterToolbarItem();
      return footerToolbarItem.getClickHandler(
        getPublicCommands?.(),
        event.target as HTMLElement
      )();
    },
    [getPublicCommands]
  );

  if (toolbars.footer.isEmpty()) {
    return null;
  }

  return (
    <div className={styles.footerToolbar} data-hook={FOOTER_TOOLBAR}>
      <InsertPluginToolbar
        type="regular"
        buttons={addButtons}
        onButtonClick={onButtonClick}
        onlyShownButtons={onlyShownButtons}
        overflownItemsPosition="top"
        options={options}
        alignment={languageDir === 'rtl' ? 'right' : 'left'}
      />
    </div>
  );
};
